import { useCallback } from 'react';
import TargetIcon from '@rsos/base-assets/icons/src/TargetIcon';
import Button from '@rsos/base-components/Button';
import { useZoomAndLocationContext } from '@rsos/components/capstone/common/Map/ZoomAndLocationContext';
import { useBooleanFlagValue } from '@rsos/feature-flags';
import {
  GPX_CAPABILITIES,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_ROUTES,
  gspx,
} from '@rsos/gainsight';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import styled from '@rsos/theme/styled';
import { getMostRecentGeoLocation } from '@rsos/utils/getMostRecentLoc';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';
import type { ESRIMapProps, GoogleMapProps } from '@rsos/utils/rsosGlobal';
import {
  isPlottablePoint,
  UNGEOCODED_CALLS_FLAG,
} from '../JurisdictionView/Map/unplottableAddress';

const StyledButton = styled(Button)`
  position: relative;
  bottom: 75px;
  left: 50%;
  transform: translateX(-50%);
  color: ${({ theme }) => theme.colors.primaryBase};
  text-indent: 4px;
`;

type Props = (GoogleMapProps | ESRIMapProps) & {
  onViewJurisdiction?: () => void;
};

export const RecenterMapButton = ({
  onViewJurisdiction,
  ...options
}: Props) => {
  const { userPannedAway } = useZoomAndLocationContext();
  const ungeocodedCallsEnabled = useBooleanFlagValue(
    UNGEOCODED_CALLS_FLAG,
    false,
  );
  const selectedCall = useSelectedCall();
  const mostRecentLocation = getMostRecentGeoLocation(selectedCall);
  const canRecenterOnCall = isPlottablePoint(mostRecentLocation);
  const trackButtonClick = () => {
    gspx.trackEvent({
      name: 'recenter_map_button',
      type: GPX_EVENT_TYPES.CLICK,
      feature: GPX_FEATURES.GIS,
      capability: GPX_CAPABILITIES.GIS,
      route: GPX_ROUTES.MAP,
    });
  };

  const onClick = useCallback(() => {
    trackButtonClick();
    if (canRecenterOnCall && mostRecentLocation) {
      panMapTo({
        ...options,
        latitude: mostRecentLocation.latitude,
        longitude: mostRecentLocation.longitude,
        zoom: null,
      });
      return;
    }

    onViewJurisdiction?.();
  }, [canRecenterOnCall, mostRecentLocation, onViewJurisdiction, options]);

  if (!userPannedAway) {
    return <></>;
  }

  if (!canRecenterOnCall && !ungeocodedCallsEnabled) {
    return <></>;
  }

  if (!canRecenterOnCall && !onViewJurisdiction) {
    return <></>;
  }

  return (
    <StyledButton size="large" btnType="secondary" onClick={onClick}>
      <TargetIcon />
      {canRecenterOnCall ? 'Re-center Call' : 'Re-center'}
    </StyledButton>
  );
};
